// Where a promo-campaign code waits until the buyer reaches the checkout (#6965),
// with no Angular in it. The thin `promo-code.service.ts` sibling supplies the
// browser storage, the clock and the reporting.
//
// See design-docs/promo-campaigns.md.
import { DateTime } from 'luxon';
import { normalisePromoCode } from '@cf-shared/data-model/promo-campaign';

/**
 * The storage key. `localStorage` rather than `sessionStorage`, unlike the referral
 * invite: session storage belongs to one tab, and the emailed sign-in link opens a
 * NEW tab, so a code kept there would be lost exactly when a signed-out buyer signs
 * up. The referral survives that step only because it is claimed onto the account
 * at sign-up, which a promo code is not.
 */
export const PROMO_CODE_STORAGE_KEY = 'cs_promo_code';

/**
 * How long a stored code is kept, in days. Long enough to cover a buyer who opens
 * the sale email, signs up, and comes back a few days later; short enough that a
 * code does not linger into an unrelated visit months on. The server decides
 * whether the campaign is still running, so an old code only costs a message.
 */
export const PROMO_CODE_MAX_AGE_DAYS = 7;

/** What is kept in storage: the code, and when it was put there. */
interface StoredPromoCode {
  code: string;
  storedAt_utc: string;
}

/** A minimal view of `localStorage`, so these rules can be tested with a plain object. */
export interface PromoCodeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** What the store could not do. The values are the condition names the service reports under. */
export enum PromoCodeStorageProblem {
  /** The storage refused the write (Safari private mode does this). */
  NotStored = 'PromoCodeNotStored',
  /** The storage refused the read. */
  Unreadable = 'PromoCodeUnreadable',
  /** The stored value was not readable, and was cleared. */
  DamagedCleared = 'PromoCodeDamagedCleared',
}

/** Receives each storage problem, with the error behind it. */
export type PromoCodeProblemReporter = (problem: PromoCodeStorageProblem, err: unknown) => void;

/**
 * Keep a code. A blank code is ignored. A storage failure does not throw: the only
 * cost is that the buyer types the code into the page's "Have a code?" field.
 */
export function rememberPromoCode(storage: PromoCodeStorage, input: string, now_utc: string, onProblem: PromoCodeProblemReporter): void {
  const code = normalisePromoCode(input);
  if (!code) return;
  try {
    const payload: StoredPromoCode = { code, storedAt_utc: now_utc };
    storage.setItem(PROMO_CODE_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    onProblem(PromoCodeStorageProblem.NotStored, err);
  }
}

/**
 * The kept code, or undefined. Read, not taken: the code is sent with every checkout
 * request until a purchase completes or the server says the campaign has ended.
 * A damaged or too-old value is removed, so it is not re-read on every page load.
 */
export function readPromoCode(storage: PromoCodeStorage, now_utc: string, onProblem: PromoCodeProblemReporter): string | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(PROMO_CODE_STORAGE_KEY);
  } catch (err) {
    onProblem(PromoCodeStorageProblem.Unreadable, err);
    return undefined;
  }
  if (!raw) return undefined;

  let parsed: StoredPromoCode | undefined;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    clearPromoCode(storage);
    onProblem(PromoCodeStorageProblem.DamagedCleared, err);
    return undefined;
  }
  const code = normalisePromoCode(typeof parsed?.code === 'string' ? parsed.code : undefined);
  const storedAt = typeof parsed?.storedAt_utc === 'string' ? DateTime.fromISO(parsed.storedAt_utc, { zone: 'utc' }) : undefined;
  const now = DateTime.fromISO(now_utc, { zone: 'utc' });
  if (!code || !storedAt?.isValid || !now.isValid) {
    clearPromoCode(storage);
    onProblem(PromoCodeStorageProblem.DamagedCleared, new Error('stored promo code has no code or no valid time'));
    return undefined;
  }
  if (now.diff(storedAt, 'days').days > PROMO_CODE_MAX_AGE_DAYS) {
    clearPromoCode(storage);
    return undefined;
  }
  return code;
}

/** Forget the kept code. Never throws: a value left behind is only ignored or cleared again later. */
export function clearPromoCode(storage: PromoCodeStorage): void {
  try {
    storage.removeItem(PROMO_CODE_STORAGE_KEY);
  } catch {
    // not reported: the next read clears or ignores it again, and nothing depends on it being gone now
  }
}
